import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Input, Textarea, Btn } from "@/components/UI";
import { FileText } from "lucide-react";
import { previewPdf } from "@/lib/pdfActions";
import { LabelButtons } from "@/components/shipping/LabelButtons";
import { fmtQty } from "@/lib/products";
import { apiError } from "@/lib/customers";

const fmtDt = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "—");

function History({ items }) {
  if (!items.length) return <div className="text-xs text-slate-500" data-testid="delivery-history-empty">Henüz teslimat yok.</div>;
  return (
    <ul className="divide-y divide-slate-100 text-sm" data-testid="delivery-history">
      {items.map((s) => (
        <li key={s.id} className="py-2 flex items-start gap-3" data-testid={`delivery-history-${s.id}`}>
          <div className="flex-1">
            <div className="font-mono text-xs font-semibold">{s.shipment_no} · {fmtDt(s.shipped_at)} · {s.created_by_name}</div>
            <div className="text-xs text-slate-600">{s.lines.map((l) => `${l.product_name}: ${fmtQty(l.qty)}`).join(" · ")}</div>
            {s.delivery_note && <div className="text-[11px] text-slate-500 italic">Not: {s.delivery_note}</div>}
          </div>
          <Btn size="sm" variant="secondary" onClick={() => previewPdf(`/shipments/${s.id}/irsaliye.pdf`, `Irsaliye_${s.shipment_no}.pdf`)} data-testid={`irsaliye-${s.id}`}><FileText size={12} /> İrsaliye</Btn>
        </li>
      ))}
    </ul>
  );
}

export function DeliverModal({ orderId, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/sales-orders/${orderId}/delivery`).then((r) => {
    setD(r);
    setQty(Object.fromEntries(r.lines.map((l) => [l.line_index, l.remaining])));
  }).catch((e) => setErr(apiError(e, "Yüklenemedi")));
  useEffect(() => { if (orderId) { setErr(""); setNote(""); setTracking(""); load(); } }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!orderId) return null;

  const submit = async (force = false) => {
    const lines = Object.entries(qty).map(([i, q]) => ({ line_index: Number(i), qty: Number(q) || 0 })).filter((l) => l.qty > 0);
    if (!lines.length) return setErr("Teslim edilecek miktar giriniz");
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/sales-orders/${orderId}/deliver`, { lines, note, tracking_no: tracking, force });
      await load();
      onDone?.(r);
      if (window.confirm(`Teslimat kaydedildi (${r.shipment.shipment_no}). İrsaliyeyi açmak ister misiniz?`)) {
        previewPdf(`/shipments/${r.shipment.id}/irsaliye.pdf`, `Irsaliye_${r.shipment.shipment_no}.pdf`);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "INSUFFICIENT_STOCK") {
        const msg = det.items.map((i) => `${i.product_name}: gönderilecek ${fmtQty(i.need)}, fiziksel stok ${fmtQty(i.on_hand)}`).join("\n");
        if (window.confirm(`⚠ ${det.message}\n\n${msg}`)) { setBusy(false); return submit(true); }
      } else setErr(apiError(e, "Teslimat kaydedilemedi"));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={d ? `Teslim Et — ${d.so_no} · ${d.customer_name}` : "Teslim Et"} size="xl">
      {!d ? <div className="text-sm text-slate-500">{err || "Yükleniyor…"}</div> : (
        <div className="space-y-4" data-testid="deliver-modal">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
              {["Ürün", "Sipariş", "Bedelsiz", "Toplam Fiziksel", "Önce Teslim", "Kalan", "Fiziksel Stok", "Bu Teslimatta"].map((h) => <th key={h} className="p-1.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.line_index} className="border-b border-slate-100" data-testid={`deliver-line-${l.line_index}`}>
                  <td className="p-1.5 font-medium">{l.product_name}</td>
                  <td className="p-1.5">{fmtQty(l.qty)}</td>
                  <td className="p-1.5">{fmtQty(l.free_qty)}</td>
                  <td className="p-1.5" data-testid={`deliver-physical-${l.line_index}`}>{fmtQty(l.physical_qty)}</td>
                  <td className="p-1.5" data-testid={`deliver-done-${l.line_index}`}>{fmtQty(l.delivered_qty)}</td>
                  <td className="p-1.5 font-semibold" data-testid={`deliver-remaining-${l.line_index}`}>{fmtQty(l.remaining)}</td>
                  <td className={`p-1.5 ${l.on_hand < (Number(qty[l.line_index]) || 0) ? "text-rose-600 font-semibold" : ""}`}>{fmtQty(l.on_hand)}</td>
                  <td className="p-1.5 w-28">
                    <Input type="number" min="0" max={l.remaining} disabled={l.remaining <= 0} value={qty[l.line_index] ?? 0}
                      onChange={(e) => setQty({ ...qty, [l.line_index]: e.target.value })} data-testid={`deliver-qty-${l.line_index}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Textarea label="Not" value={note} onChange={(e) => setNote(e.target.value)} data-testid="deliver-note" />
            <Input label="Kargo Takip No" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="deliver-tracking" />
          </div>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="deliver-error">{err}</div>}
          <div className="flex justify-between gap-2">
            <div className="flex gap-2"><LabelButtons orderId={orderId} soNo={d.so_no} status={d.status || "approved"} testid="shipping-label-btn" withPrint /></div>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={onClose} data-testid="deliver-cancel">Kapat</Btn>
              {d.delivery_status !== "completed" && <Btn onClick={() => submit(false)} disabled={busy} data-testid="deliver-submit">{busy ? "Kaydediliyor…" : "Teslimatı Kaydet"}</Btn>}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-1">Teslimat Geçmişi</div>
            <History items={d.history} />
          </div>
        </div>
      )}
    </Modal>
  );
}
